/**
 * User attributes stored on Mongo/API as `user.custom.<key>`.
 * Same keys and shape as SIS `POST /sis/import/users` and admin
 * `POST /admin/import/users` (see serverless/sis-import/specs/adminUser.specs.yaml).
 *
 * This ticket exposes only a subset in the create-user UI. Other documented SIS
 * keys (e.g. preEnrollment) can be added here later instead of a free-form
 * custom editor, to stay aligned with SIS and reporting.
 */
export type SisUserCustomFieldKey = 'studentId' | 'degreeCourse';

/** Matches SIS / adminUser / userPatch OpenAPI maxLength for these fields. */
export const SIS_USER_CUSTOM_FIELD_MAX_LENGTH = 100;

export type SisUserCustomFieldConfig = {
  /** Key under `user.custom` in API payloads */
  key: SisUserCustomFieldKey;
  /** Admin UI label */
  label: string;
  placeholder: string;
  /** CSV column name (matches SIS bulk-import CSV conventions) */
  csvColumn: SisUserCustomFieldKey;
  helperText?: string;
  maxLength: number;
  /** Shown on user profile after create (AC: only studentId is editable today) */
  editableOnUserProfile: boolean;
};

export const SIS_USER_CUSTOM_FIELDS_ON_CREATE: SisUserCustomFieldConfig[] = [
  {
    key: 'studentId',
    label: 'Student ID',
    placeholder: 'Student ID',
    csvColumn: 'studentId',
    helperText: 'Optional',
    maxLength: SIS_USER_CUSTOM_FIELD_MAX_LENGTH,
    editableOnUserProfile: true,
  },
  {
    key: 'degreeCourse',
    label: 'Degree Course',
    placeholder: 'Degree Course',
    csvColumn: 'degreeCourse',
    helperText: 'Optional',
    maxLength: SIS_USER_CUSTOM_FIELD_MAX_LENGTH,
    editableOnUserProfile: false,
  },
];

export const SIS_USER_CUSTOM_FIELD_KEYS = SIS_USER_CUSTOM_FIELDS_ON_CREATE.map(
  (field) => field.key,
);

export const isSisUserCustomFieldKey = (
  field: string,
): field is SisUserCustomFieldKey =>
  (SIS_USER_CUSTOM_FIELD_KEYS as readonly string[]).includes(field);

export const getSisUserCustomFieldCountLabel = (
  value: string | undefined,
  maxLength: number = SIS_USER_CUSTOM_FIELD_MAX_LENGTH,
) => `${(value || '').length}/${maxLength}`;

/** Grow input width with content; stay compact for short values. */
export const getSisUserCustomFieldInputWidth = (
  value: string | undefined,
  {
    minPx = 140,
    maxPx = 320,
    pxPerChar = 8,
  }: { minPx?: number; maxPx?: number; pxPerChar?: number } = {},
) => {
  const contentWidth = Math.max((value || '').length, 10) * pxPerChar + 16;
  return Math.min(maxPx, Math.max(minPx, contentWidth));
};

export const getSisUserCustomFieldError = (
  value: string | undefined,
  label = 'Field',
  maxLength: number = SIS_USER_CUSTOM_FIELD_MAX_LENGTH,
) => {
  if (!value || !value.trim()) {
    return null;
  }
  if (value.trim().length > maxLength) {
    return `${label} must be ${maxLength} characters or fewer`;
  }
  return null;
};
